import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { Stack, Constrain, Text } from '@moda/om';
import { BehindFeatureFlag } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { PaymentSource } from '../../constants';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useUser } from '../../hooks/useUser';
import {
  CartUpdateAction,
  CartUpdatePropertyName,
  useCart,
  useCartItems,
  useUpdateCartMutation
} from '../../hooks/useCart';
import { useAddressesQuery } from '../../hooks/useAddress';
import { usePaymentMethodsQuery } from '../../hooks/usePaymentMethods';
import { useCheckout, useCheckoutStartTracking } from '../../hooks/useCheckout';
import { CheckoutFormStatesProvider } from '../../components/CheckoutFormStates';
import { RedirectWithStatusCode } from '../../components/RedirectWithStatusCode';
import { AccountEmailInUseLink } from '../../components/AccountEmailInUseLink';
import { Loading } from '../../components/Loading';
import { PageMeta } from '../../components/PageMeta';
import { CheckoutAuthentication } from '../../components/Authentication';
import { CheckoutSection } from '../../components/CheckoutSection';
import { LineItemList } from '../../components/LineItemList';
import { CheckoutHeader } from '../../components/CheckoutHeader';
import { CheckoutPaymentSummary } from '../../components/PaymentSummary';
import { ShippingMethod } from '../../components/ShippingMethod';
import { CheckoutPaymentMethod } from '../../components/PaymentMethod';
import { CheckoutShippingAddress } from '../../components/ShippingAddress';
import { CheckoutAmazonShippingAndPayment } from '../../components/AmazonShippingAndPayment';
import { CheckoutPayPalShippingAndPayment } from '../../components/PayPalShippingAndPayment';
import { CheckoutPaymentSources } from '../../components/CheckoutPaymentSources';
import { CheckoutSiteCredit } from '../../components/CheckoutSiteCredit';
import { CheckoutGiftSection } from '../../components/CheckoutGiftSection';
import { CheckoutGiftSectionError } from '../../components/CheckoutGiftSection/CheckoutGiftSectionError';
import { CheckoutNotifications } from '../../components/CheckoutNotifications';

import './CheckoutPage.scss';

export const CheckoutPage: React.FC = () => {
  const {
    user: { accessToken, isAmazonConnected }
  } = useUser();
  const { cart, loading: loadingCart, loaded: cartLoaded, error: cartError } = useCart();
  const { addresses, loading: loadingAddresses, error: addressesError } = useAddressesQuery();
  const [updateCart] = useUpdateCartMutation();
  const {
    attributes: { priceChangedMessage }
  } = cart;

  const {
    data: paymentMethods,
    loading: loadingPaymentMethods,
    error: paymentMethodsError
  } = usePaymentMethodsQuery();
  const { availableNowItems, preorderItems, unavailableItems } = useCartItems();
  const {
    isShippingAddressSelected,
    isShippingMethodSelected,
    isPaymentMethodSelected,
    canPlaceOrder
  } = useCheckout();

  useCheckoutStartTracking();

  if (loadingCart || loadingAddresses || loadingPaymentMethods) return <Loading />;

  if (accessToken && (!addresses || !paymentMethods)) return <Loading />;

  if (cartLoaded && !cart.attributes.cartItemsCount)
    return (
      <RedirectWithStatusCode
        statusCode={StatusCodes.MOVED_TEMPORARILY}
        to={hrefFor.ShoppingBagPage()}
      />
    );

  if (cartError) throwQueryError(cartError);
  if (addressesError) throwQueryError(addressesError);
  if (paymentMethodsError) throwQueryError(paymentMethodsError);

  const changePayment = async () => {
    if (isAmazonConnected) {
      await updateCart(CartUpdateAction.PaymentSource, {
        [CartUpdatePropertyName.PaymentSource]: PaymentSource.Amazon
      });
    }
  };

  return (
    <CheckoutFormStatesProvider>
      <PageMeta title="Checkout" />

      <div className="CheckoutPage">
        <div>
          <CheckoutHeader />

          {isShippingAddressSelected &&
            isShippingMethodSelected &&
            isPaymentMethodSelected &&
            !canPlaceOrder &&
            accessToken && (
              <div className="CheckoutPage__error">
                <Constrain>Please resolve the errors below before proceeding.</Constrain>
              </div>
            )}

          <CheckoutGiftSectionError />
        </div>

        {!accessToken && (
          <Constrain>
            <CheckoutAuthentication changePayment={changePayment} />
          </Constrain>
        )}

        {accessToken && (
          <Constrain>
            <Stack space={4}>
              <AccountEmailInUseLink />
              <CheckoutPaymentSources />
              <div className="CheckoutPage__main">
                <div className="CheckoutPage__form">
                  <BehindFeatureFlag
                    flagName="address-based-currency"
                    showWhenValueIs={true}
                    showIfFlagIsUnavailable={false}
                  >
                    <CheckoutNotifications priceChangedMessage={priceChangedMessage} />
                  </BehindFeatureFlag>
                  {(cart.attributes.paymentSource === PaymentSource.Adyen ||
                    cart.attributes.paymentSource === PaymentSource.Affirm ||
                    cart.attributes.paymentSource === PaymentSource.Moda) && (
                    <>
                      <CheckoutShippingAddress />
                      {isShippingAddressSelected && availableNowItems.length > 0 && (
                        <ShippingMethod />
                      )}

                      <CheckoutGiftSection />

                      {isShippingAddressSelected && isShippingMethodSelected && (
                        <CheckoutPaymentMethod />
                      )}
                    </>
                  )}

                  {cart.attributes.paymentSource === PaymentSource.Amazon && (
                    <>
                      <CheckoutAmazonShippingAndPayment />
                      {isShippingAddressSelected &&
                        isPaymentMethodSelected &&
                        availableNowItems.length > 0 && <ShippingMethod />}

                      <CheckoutGiftSection />
                    </>
                  )}

                  {cart.attributes.paymentSource === PaymentSource.PayPal && (
                    <>
                      <CheckoutPayPalShippingAndPayment />
                      {isShippingAddressSelected &&
                        isPaymentMethodSelected &&
                        availableNowItems.length > 0 && <ShippingMethod />}

                      <CheckoutGiftSection />
                    </>
                  )}

                  {isShippingAddressSelected &&
                    isShippingMethodSelected &&
                    isPaymentMethodSelected && (
                      <>
                        <CheckoutSiteCredit />
                        <CheckoutSection title="Review Order Items" divider={false}>
                          <Stack space={4}>
                            {availableNowItems.length > 0 && (
                              <LineItemList
                                title="Available Now"
                                theme="simple"
                                items={availableNowItems}
                                showPriceWithVAT
                              />
                            )}

                            {preorderItems.length > 0 && (
                              <LineItemList
                                title="Preorder"
                                theme="simple"
                                items={preorderItems}
                                showPriceWithVAT
                              />
                            )}

                            {unavailableItems.length > 0 && (
                              <LineItemList
                                title="Unavailable"
                                theme="simple"
                                items={unavailableItems}
                                showPriceWithVAT
                              />
                            )}
                          </Stack>
                        </CheckoutSection>
                      </>
                    )}
                </div>
                <Stack className="CheckoutPage__summary" space={4}>
                  <CheckoutPaymentSummary />
                  <Text treatment="body1">
                    **The amount of sales tax due depends on the sales tax laws of the location to
                    which you are shipping
                  </Text>
                </Stack>
              </div>
            </Stack>
          </Constrain>
        )}
      </div>
    </CheckoutFormStatesProvider>
  );
};
